import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { AmountInput, Button, CurrenciesUSD } from '@column/column-ui-kit';

import { PopoverAction, PopoverBase, PopoverBaseProps } from '~/components/Popover/Base';
import { FormElement, FormLabel } from '~/styles';

export interface PopoverFilterBalanceData {
  balanceGte?: number;
  balanceLte?: number;
}

interface PopoverFilterBalanceProps extends PopoverBaseProps {
  data: PopoverFilterBalanceData;
  onSubmit?: (data: PopoverFilterBalanceData) => void;
}

const Wrapper = styled(PopoverBase)`
  --popover-padding: 0px;
  --popover-width: 248px;
  --popover-left: 0px;
`;

const Grid = styled.div`
  display: grid;
  grid-gap: 16px;
  padding: 12px 12px 0 12px;
`;

export const PopoverFilterBalance: React.FC<PopoverFilterBalanceProps> = ({ data, onSubmit, ...popoverProps }) => {
  const [currentData, setCurrentData] = useState<PopoverFilterBalanceData>(data ?? {});

  const handleResetClick = useCallback(() => {
    setCurrentData({});

    if (onSubmit) {
      onSubmit({});
    }
  }, [onSubmit]);

  const handleApplyClick = useCallback(() => {
    if (onSubmit) {
      onSubmit(JSON.parse(JSON.stringify(currentData)));
    }
  }, [currentData, onSubmit]);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleApplyClick();
      }
    },
    [handleApplyClick]
  );

  useEffect(() => {
    setCurrentData(data);
  }, [data]);

  return (
    <Wrapper {...popoverProps}>
      <Grid>
        <FormElement>
          <FormLabel>Minimum</FormLabel>
          <AmountInput
            size="small"
            currencyList={CurrenciesUSD}
            fixedCurrencyCode="USD"
            value={Number(currentData.balanceGte ?? 0)}
            onChange={(value) => {
              setCurrentData((s) => ({ ...s, balanceGte: value === 0 ? undefined : Number(value) }));
            }}
            onKeyDown={handleKeyPress}
          />
        </FormElement>

        <FormElement>
          <FormLabel>Maximum</FormLabel>
          <AmountInput
            size="small"
            currencyList={CurrenciesUSD}
            fixedCurrencyCode="USD"
            value={Number(currentData.balanceLte ?? 0)}
            onChange={(value) => {
              setCurrentData((s) => ({ ...s, balanceLte: value === 0 ? undefined : Number(value) }));
            }}
            onKeyDown={handleKeyPress}
          />
        </FormElement>
      </Grid>

      <PopoverAction>
        <Button size="small" variant="secondary" onClick={handleResetClick}>
          Reset
        </Button>
        <Button size="small" onClick={handleApplyClick}>
          Apply
        </Button>
      </PopoverAction>
    </Wrapper>
  );
};
